import { z } from "zod";

import {
  instructionActionValues,
  instructionViewTimescaleValues,
  instructionSchema as storedInstructionSchema,
} from "@schemas/instruction";
import { hexStringSchema, sortValues } from "@schemas/misc";
import { eip2098SignatureSchema } from "@schemas/sign";

export const instructionSchema = z.object({
  action: hexStringSchema,
  arguments: hexStringSchema,
  maxExecutions: hexStringSchema,
  salt: hexStringSchema,
  nickname: z.string().nullish(),
});
export type Instruction = z.infer<typeof instructionSchema>;

// api schemas

export const instructionGetAllRequestSchema = z.object({
  address: hexStringSchema,
  chainId: z.number(),
});
export type InstructionGetAllRequest = z.infer<
  typeof instructionGetAllRequestSchema
>;

export const instructionGetAllResponseSchema = z.array(storedInstructionSchema);
export type InstructionGetAllResponse = z.infer<
  typeof instructionGetAllResponseSchema
>;

export const instructionGetSummaryRequestSchema = z.object({
  address: hexStringSchema,
  chainId: z.number(),
  instructionIds: z.array(hexStringSchema),
  from: z.string(),
  to: z.string(),
  skip: z.number().optional(),
  take: z.number().optional(),
  sort: z.enum(sortValues),
  timeScale: z.enum(instructionViewTimescaleValues).optional(),
});
export type InstructionGetSummaryRequest = z.infer<
  typeof instructionGetSummaryRequestSchema
>;

export const instructionBuildRequestSchema = z.object({
  address: hexStringSchema,
  chainId: z.number(),
  salt: z.number(),
  maxExecutions: z.number(),
  actionArguments: z.record(z.enum(instructionActionValues), z.unknown()),
});
export type InstructionBuildRequest = z.infer<
  typeof instructionBuildRequestSchema
>;

export const instructionBuildResponseSchema = instructionSchema;
export type InstructionBuildResponse = z.infer<
  typeof instructionBuildResponseSchema
>;

export const instructionActivateRequestSchema = z.object({
  nickname: z.string().optional(),
  address: hexStringSchema,
  chainId: z.number(),
  salt: hexStringSchema,
  maxExecutions: hexStringSchema,
  action: hexStringSchema,
  arguments: hexStringSchema,
  activationSignature: eip2098SignatureSchema,
});
export type InstructionActivateRequest = z.infer<
  typeof instructionActivateRequestSchema
>;

export const instructionActivateResponseSchema = z.object({
  instructionId: hexStringSchema,
});
export type InstructionActivateResponse = z.infer<
  typeof instructionActivateResponseSchema
>;

export const instructionDeactivateRequestSchema = z.object({
  address: hexStringSchema,
  chainId: z.number(),
  deactivation: z.object({
    instructionId: hexStringSchema,
  }),
  deactivationSignature: eip2098SignatureSchema,
});
export type InstructionDeactivateRequest = z.infer<
  typeof instructionDeactivateRequestSchema
>;

export const instructionDeactivateResponseSchema = z.unknown();
export type InstructionDeactivateResponse = z.infer<
  typeof instructionDeactivateResponseSchema
>;

export const instructionDetailsRequestSchema = z.object({
  address: hexStringSchema,
  instructionId: hexStringSchema,
  chainId: z.number(),
});
export type InstructionDetailsRequest = z.infer<
  typeof instructionDetailsRequestSchema
>;

export const instructionDetailsResponseSchema = z.object({
  action: hexStringSchema,
  actionArguments: z.unknown(),
  actionName: z.string().nullish(),
  salt: hexStringSchema,
  maxExecutions: hexStringSchema,
  nickname: z.string().nullish(),
});
export type InstructionDetailsResponse = z.infer<
  typeof instructionDetailsResponseSchema
>;

export const getMaxPriorityFeePerGasEstimateSchema = z.object({
  chainId: z.number(),
});
export type GetMaxPriorityFeePerGasEstimateRequest = z.infer<
  typeof getMaxPriorityFeePerGasEstimateSchema
>;

export const getMaxPriorityFeePerGasEstimateResponseSchema = z.object({
  slowMaxPriorityFeeEstimate: z.number(),
  normalMaxPriorityFeeEstimate: z.number(),
  fastMaxPriorityFeeEstimate: z.number(),
});
export type GetMaxPriorityFeePerGasEstimateResponse = z.infer<
  typeof getMaxPriorityFeePerGasEstimateResponseSchema
>;
